import React, { useState, useEffect, useRef } from 'react';
import { Star, ChevronLeft, ChevronRight, CheckCircle, Quote } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const reviews = [
  {
    name: 'Thomas Gravesen',
    initials: 'TG',
    rating: 5,
    text: '“Vejle Padelcenter. Vi har brugt BM Maler til vores projekt. Det har været en god oplevelse med god rådgivning omkring farvevalg og materialer. Arbejdet er udført til tiden og i høj kvalitet. Kan varmt anbefale BM Maler.”',
    source: 'Google',
    date: 'for 2 år siden'
  },
  {
    name: 'Kristine Lingren Larsen Albertsen',
    initials: 'KA',
    rating: 5,
    text: '“Kun rosende ord herfra ~ ALT malearbejde og tapetsering, 100 % professionelt udført!”',
    source: 'Facebook',
    date: 'for 2 år siden'
  },
  {
    name: 'Vibeke Dissing',
    initials: 'VD',
    rating: 5,
    text: '“Super flot arbejde! Nu står huset lyst og skarpt. Samarbejdet var helt perfekt.”',
    source: 'Google',
    date: 'for 2 år siden'
  },
  {
    name: 'Søren Christensen',
    initials: 'SC',
    rating: 5,
    text: '“Flot arbejde der er udført i vores bilhus i Vejle, og imødekommende personale.”',
    source: 'Facebook',
    date: 'for 2 år siden'
  },
  {
    name: 'Flemming Lund',
    initials: 'FL',
    rating: 5,
    text: '“Fik udført spartling og opsætning af filt i stuen med kort varsel og til aftalt pris.”',
    source: 'Google',
    date: 'for 2 år siden'
  },
  {
    name: 'Rasmus Egsgaard',
    initials: 'RE',
    rating: 5,
    text: '“En super god service og rigtig godt arbejde. Det var en flink og dygtig maler som udførte arbejdet og det blev gjort rigtig godt og med et super flot resultat. Har brugt BM Maler flere gange og hver gang meget tilfreds. Så vil klart anbefale BM Maler 👌”',
    source: 'Google',
    date: 'for 2 år siden'
  },
  {
    name: 'Benjamin Madsen',
    initials: 'BM',
    rating: 5,
    text: '“Super godt og effektivt arbejde :-)”',
    source: 'Google',
    date: 'for 2 år siden'
  },
  {
    name: 'Ole Christiansen',
    initials: 'OC',
    rating: 5,
    text: '“Har brugt BM flere gange og altid top godt arbejde”',
    source: 'Facebook',
    date: 'for 2 år siden'
  },
  {
    name: 'Lau Rasmussen',
    initials: 'LR',
    rating: 5,
    text: '“5 stjerner – super god service, hurtig respons og et perfekt resultat hele vejen igennem.”',
    source: 'Facebook',
    date: 'for 2 år siden'
  },
];

function GoogleIcon({ className = "w-3.5 h-3.5" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none">
      <path
        d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.8-2.4 3.65v3.03h3.88c2.27-2.09 3.66-5.17 3.66-9.12z"
        fill="#4285F4"
      />
      <path
        d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.03c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.1-6.72-4.93H1.27v3.13C3.25 21.31 7.31 24 12 24z"
        fill="#34A853"
      />
      <path
        d="M5.28 14.29c-.25-.72-.38-1.49-.38-2.29s.13-1.57.38-2.29V6.57H1.27C.46 8.2 0 10.04 0 12s.46 3.8 1.27 5.43l4.01-3.14z"
        fill="#FBBC05"
      />
      <path
        d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.25 2.69 1.27 6.57l4.01 3.14c.95-2.83 3.6-4.96 6.72-4.96z"
        fill="#EA4335"
      />
    </svg>
  );
}

function FacebookIcon({ className = "w-3.5 h-3.5" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  );
}

const slideVariants = {
  enter: (direction) => {
    return {
      x: direction > 0 ? 80 : -80,
      opacity: 0,
    };
  },
  center: {
    x: 0,
    opacity: 1,
    transition: {
      x: { type: 'spring', stiffness: 380, damping: 30 },
      opacity: { duration: 0.2, ease: 'easeOut' },
    },
  },
  exit: (direction) => {
    return {
      x: direction > 0 ? -80 : 80,
      opacity: 0,
      transition: {
        x: { type: 'spring', stiffness: 380, damping: 30 },
        opacity: { duration: 0.15, ease: 'easeIn' },
      },
    };
  },
};

export default function ReviewSlider() {
  // Desktop pagination: 2 groups of 3 reviews
  const desktopTotalPages = Math.ceil(reviews.length / 3);
  const [desktopPage, setDesktopPage] = useState(0);
  const [mobileIndex, setMobileIndex] = useState(0);
  const [direction, setDirection] = useState(1);

  const handleDesktopPrev = () => {
    setDirection(-1);
    setDesktopPage((prev) => (prev === 0 ? desktopTotalPages - 1 : prev - 1));
  };

  const handleDesktopNext = () => {
    setDirection(1);
    setDesktopPage((prev) => (prev + 1) % desktopTotalPages);
  };

  const handleMobilePrev = () => {
    setDirection(-1);
    setMobileIndex((prev) => (prev === 0 ? reviews.length - 1 : prev - 1));
  };

  const handleMobileNext = () => {
    setDirection(1);
    setMobileIndex((prev) => (prev + 1) % reviews.length);
  };

  const currentDesktopReviews = reviews.slice(desktopPage * 3, desktopPage * 3 + 3);

  return (
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      {/* Header */}
      <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
        <div className="inline-flex items-center gap-2 bg-[#F1B528]/10 border border-[#F1B528]/20 px-3.5 py-1.5 rounded-full text-[#F1B528] text-xs font-bold uppercase tracking-wider">
          <Star className="w-3.5 h-3.5 fill-[#F1B528] text-[#F1B528]" />
          <span>Google &amp; Facebook Anmeldelser</span>
        </div>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white pt-4">
          Hvad siger kunderne om vores malerarbejde?
        </h2>
        <p className="text-slate-400 text-sm sm:text-base leading-relaxed">
          Vores faglærte malere gør sig umage, og det afspejler sig i de flotte anmeldelser fra vores tilfredse kunder på Google og Facebook.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-4 pt-2 text-xs font-medium text-slate-300">
          <div className="inline-flex items-center gap-1.5 bg-slate-900/60 border border-slate-800 px-3 py-1.5 rounded-full">
            <GoogleIcon className="w-4 h-4 shrink-0" />
            <span>4,9 / 5 på Google</span>
          </div>
          <div className="inline-flex items-center gap-1.5 bg-slate-900/60 border border-slate-800 px-3 py-1.5 rounded-full">
            <FacebookIcon className="w-4 h-4 text-[#1877F2] shrink-0" />
            <span>5,0 / 5 på Facebook</span>
          </div>
        </div>
      </div>

      {/* Desktop Grid (3 Columns) with slide + fade and fixed height */}
      <div className="hidden lg:block relative">
        <div className="relative h-[340px] overflow-hidden">
          <AnimatePresence initial={false} custom={direction} mode="wait">
            <motion.div
              key={desktopPage}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              className="grid grid-cols-3 gap-8 absolute inset-0 w-full"
            >
              {currentDesktopReviews.map((review) => (
                <div
                  key={review.name}
                  className="bg-slate-900/40 border border-slate-800/80 rounded-3xl p-7 shadow-xl flex flex-col justify-between h-full relative group hover:border-[#F1B528]/30 transition-colors duration-300"
                >
                  {/* Quote Icon Background */}
                  <Quote className="absolute top-6 right-7 w-12 h-12 text-slate-800/40 pointer-events-none group-hover:text-[#F1B528]/5 transition-colors duration-300" />

                  {/* Top content: Rating + Quote Text with static space */}
                  <div className="relative z-10 flex flex-col flex-1">
                    <div className="flex gap-1 mb-4">
                      {[...Array(review.rating)].map((_, i) => (
                        <Star key={i} className="w-5 h-5 fill-[#F1B528] text-[#F1B528]" />
                      ))}
                    </div>
                    <div className="flex-1 flex items-start">
                      <p className="text-slate-200 font-medium italic leading-relaxed text-sm line-clamp-4">
                        {review.text}
                      </p>
                    </div>
                  </div>

                  {/* Bottom: Author info & source badge (locked to bottom) */}
                  <div className="pt-5 mt-auto border-t border-slate-800/60 flex justify-between items-center relative z-10">
                    <div className="flex items-center gap-3.5">
                      {/* Profile Circle */}
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#F1B528] to-[#D99C1B] text-slate-950 flex items-center justify-center font-black text-xs shadow-md shrink-0">
                        {review.initials}
                      </div>
                      <div className="min-w-0">
                        <h4 className="font-bold text-white text-sm flex items-center gap-1.5 truncate">
                          <span className="truncate">{review.name}</span>
                          <CheckCircle className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                        </h4>
                        <span className="text-xs text-slate-400 flex items-center gap-1.5">
                          {review.source === 'Google' ? (
                            <GoogleIcon className="w-3.5 h-3.5 inline-block shrink-0" />
                          ) : (
                            <FacebookIcon className="w-3.5 h-3.5 inline-block text-[#1877F2] shrink-0" />
                          )}
                          <span>Anmeldt på {review.source}</span>
                        </span>
                      </div>
                    </div>
                    <span className="text-xs font-bold text-[#F1B528] bg-[#F1B528]/10 border border-[#F1B528]/20 px-2.5 py-1 rounded-full shrink-0">
                      {review.rating.toFixed(1)}
                    </span>
                  </div>
                </div>
              ))}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Desktop Navigation & Dots */}
        <div className="flex items-center justify-center gap-6 mt-10">
          <button
            onClick={handleDesktopPrev}
            className="p-2.5 rounded-full bg-slate-900/80 hover:bg-[#F1B528] hover:text-slate-950 text-white border border-slate-800 hover:border-[#F1B528] transition-all duration-200 cursor-pointer"
            aria-label="Forrige anmeldelser"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="flex gap-2">
            {[...Array(desktopTotalPages)].map((_, i) => (
              <button
                key={i}
                onClick={() => {
                  setDirection(i > desktopPage ? 1 : -1);
                  setDesktopPage(i);
                }}
                className={`h-2 rounded-full transition-all duration-300 cursor-pointer ${
                  desktopPage === i ? 'w-8 bg-[#F1B528]' : 'w-2 bg-slate-700 hover:bg-slate-500'
                }`}
                aria-label={`Gå til side ${i + 1}`}
              />
            ))}
          </div>
          <button
            onClick={handleDesktopNext}
            className="p-2.5 rounded-full bg-slate-900/80 hover:bg-[#F1B528] hover:text-slate-950 text-white border border-slate-800 hover:border-[#F1B528] transition-all duration-200 cursor-pointer"
            aria-label="Næste anmeldelser"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Mobile/Tablet Slider with slide + fade and fixed height */}
      <div className="lg:hidden relative">
        <div className="relative h-[310px] overflow-hidden rounded-3xl">
          <AnimatePresence initial={false} custom={direction} mode="wait">
            <motion.div
              key={mobileIndex}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              className="absolute inset-0 w-full h-full"
            >
              <div className="bg-slate-900/40 border border-slate-800/80 rounded-3xl p-7 shadow-xl flex flex-col justify-between h-full relative">
                <Quote className="absolute top-6 right-7 w-12 h-12 text-slate-800/40 pointer-events-none" />

                <div className="relative z-10 flex flex-col flex-1">
                  <div className="flex gap-1 mb-4">
                    {[...Array(reviews[mobileIndex].rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-[#F1B528] text-[#F1B528]" />
                    ))}
                  </div>
                  <div className="flex-1 flex items-start">
                    <p className="text-slate-200 font-medium italic leading-relaxed text-sm line-clamp-5">
                      {reviews[mobileIndex].text}
                    </p>
                  </div>
                </div>

                <div className="pt-5 mt-auto border-t border-slate-800/60 flex justify-between items-center relative z-10">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#F1B528] to-[#D99C1B] text-slate-950 flex items-center justify-center font-black text-xs shadow-md shrink-0">
                      {reviews[mobileIndex].initials}
                    </div>
                    <div className="min-w-0">
                      <h4 className="font-bold text-white text-sm flex items-center gap-1.5 truncate">
                        <span className="truncate">{reviews[mobileIndex].name}</span>
                        <CheckCircle className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                      </h4>
                      <span className="text-xs text-slate-400 flex items-center gap-1.5">
                        {reviews[mobileIndex].source === 'Google' ? (
                          <GoogleIcon className="w-3.5 h-3.5 inline-block shrink-0" />
                        ) : (
                          <FacebookIcon className="w-3.5 h-3.5 inline-block text-[#1877F2] shrink-0" />
                        )}
                        <span>Anmeldt på {reviews[mobileIndex].source}</span>
                      </span>
                    </div>
                  </div>
                  <span className="text-xs font-bold text-[#F1B528] bg-[#F1B528]/10 border border-[#F1B528]/20 px-2.5 py-1 rounded-full shrink-0">
                    {reviews[mobileIndex].rating.toFixed(1)}
                  </span>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Mobile Navigation Controls & Dots */}
        <div className="flex items-center justify-center gap-4 mt-8">
          <button
            onClick={handleMobilePrev}
            className="p-3 rounded-full bg-slate-900 hover:bg-[#F1B528] hover:text-slate-950 text-white border border-slate-800 hover:border-[#F1B528] transition-all duration-200 cursor-pointer"
            aria-label="Forrige anmeldelse"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex gap-1.5">
            {reviews.map((_, idx) => (
              <button
                key={idx}
                onClick={() => {
                  setDirection(idx > mobileIndex ? 1 : -1);
                  setMobileIndex(idx);
                }}
                className={`h-2 rounded-full transition-all duration-300 cursor-pointer ${
                  mobileIndex === idx ? 'w-6 bg-[#F1B528]' : 'w-2 bg-slate-700'
                }`}
                aria-label={`Gå til anmeldelse ${idx + 1}`}
              />
            ))}
          </div>
          <button
            onClick={handleMobileNext}
            className="p-3 rounded-full bg-slate-900 hover:bg-[#F1B528] hover:text-slate-950 text-white border border-slate-800 hover:border-[#F1B528] transition-all duration-200 cursor-pointer"
            aria-label="Næste anmeldelse"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
